import React, { useContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import AlarmIcon from "@material-ui/icons/Alarm";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import CheckIcon from "@material-ui/icons/Check";
import { toast } from "react-toastify";

import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import DateRangeIcon from "@material-ui/icons/DateRange";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  subDays,
  subMonths
} from "date-fns";

import useTickets from "../../hooks/useTickets";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import Chart from "./Chart";
import MonthlyChart from "./MonthlyChart";
import FunnelConversionDashboard from "./FunnelConversionDashboard";
import ConnectionsTicketsDashboard from "./ConnectionsTicketsDashboard";
import NpsQuickOverviewDashboard from "./NpsQuickOverviewDashboard";
import TicketsWhatsappSelect from "../../components/TicketsWhatsappSelect";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  fixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "visible",
    flexDirection: "column",
    minHeight: 240,
  },
  monthlyChartPaper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  customFixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    height: 120,
  },
  remindersPaper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    minHeight: 280,
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [todayReminders, setTodayReminders] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);

  // Filtro global de conexão — "" = todas que o usuário tem acesso
  // (GET /whatsapp já filtra por GetAllowedWhatsappIds: admin vê tudo,
  // usuário comum só o permitido). Controla todas as seções da Dashboard.
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");

  // Filtro global de período (mês atual por padrão)
  const [selectedPeriod, setSelectedPeriod] = useState("thisMonth");
  const [customStartDate, setCustomStartDate] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [customEndDate, setCustomEndDate] = useState(format(endOfMonth(new Date()), "yyyy-MM-dd"));

  const getComputedDateRange = () => {
    const now = new Date();
    switch (selectedPeriod) {
      case "last7":
        return {
          start: format(startOfDay(subDays(now, 6)), "yyyy-MM-dd"),
          end: format(endOfDay(now), "yyyy-MM-dd"),
          label: "Últimos 7 dias"
        };
      case "last15":
        return {
          start: format(startOfDay(subDays(now, 14)), "yyyy-MM-dd"),
          end: format(endOfDay(now), "yyyy-MM-dd"),
          label: "Últimos 15 dias"
        };
      case "last30":
        return {
          start: format(startOfDay(subDays(now, 29)), "yyyy-MM-dd"),
          end: format(endOfDay(now), "yyyy-MM-dd"),
          label: "Últimos 30 dias"
        };
      case "thisMonth":
        return {
          start: format(startOfMonth(now), "yyyy-MM-dd"),
          end: format(endOfMonth(now), "yyyy-MM-dd"),
          label: "Este mês"
        };
      case "lastMonth": {
        const last = subMonths(now, 1);
        return {
          start: format(startOfMonth(last), "yyyy-MM-dd"),
          end: format(endOfMonth(last), "yyyy-MM-dd"),
          label: "Mês anterior"
        };
      }
      case "custom":
        return {
          start: customStartDate,
          end: customEndDate,
          label: "Personalizado"
        };
      default:
        return {
          start: format(startOfMonth(now), "yyyy-MM-dd"),
          end: format(endOfMonth(now), "yyyy-MM-dd"),
          label: "Este mês"
        };
    }
  };

  const { start: activeStartDate, end: activeEndDate, label: activePeriodLabel } = getComputedDateRange();

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);

  var userQueueIds = [];
  if (user.queues && user.queues.length > 0) {
    userQueueIds = user.queues.map((q) => q.id);
  }

  const GetTickets = (status, showAll, withUnreadMessages) => {
    const { count } = useTickets({
      status: status,
      showAll: showAll,
      withUnreadMessages: withUnreadMessages,
      queueIds: JSON.stringify(userQueueIds),
      whatsappId: selectedWhatsappId || undefined,
      startDate: activeStartDate,
      endDate: activeEndDate,
    });
    return count;
  };

  const fetchReminders = async () => {
    setLoadingReminders(true);
    try {
      const { data } = await api.get("/contacts/reminders", {
        params: {
          whatsappId: selectedWhatsappId || undefined,
        },
      });

      setTodayReminders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao carregar lembretes:", err);
      toastError(err);
    }
    setLoadingReminders(false);
  };

  useEffect(() => {
    fetchReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWhatsappId]);

  const handleOpenSendModal = (rem) => {
    setSelectedReminder(rem);
    let templateText = rem.template || "Olá {nome}, passando para lembrar sobre nosso atendimento!";
    templateText = templateText.replace(/{nome}/gi, rem.contactName || "Cliente");
    setMessageText(templateText);
    setSendModalOpen(true);
  };

  const handleSendReminderMessage = async () => {
    if (!selectedReminder) return;
    setSending(true);
    try {
      // 1. Create or get ticket for this contact
      // whatsappId explícito (conexão do próprio contato) — sem isso cai no
      // GetDefaultWhatsApp, que exige conexão isDefault/usuário com whatsapp
      // fixo, nenhum dos dois configurado neste projeto (mesmo bug dos itens
      // 39/47 do HISTORICO_AJUSTES.md, aqui nesse fluxo específico).
      const { data: ticket } = await api.post("/tickets", {
        contactId: selectedReminder.contactId,
        userId: user?.id,
        status: "open",
        whatsappId: selectedReminder.contactWhatsappId,
      });

      // 2. Mark reminder as completed in contact extraInfo
      let currentInfo = [...(selectedReminder.contactExtraInfo || [])];
      const idx = currentInfo.findIndex((i) => i.name === "LembretesCliente");
      if (idx >= 0) {
        let list = JSON.parse(currentInfo[idx].value);
        list = list.map((r) =>
          r.id === selectedReminder.id ? { ...r, status: "completed" } : r
        );
        currentInfo[idx].value = JSON.stringify(list);
        await api.put(`/contacts/${selectedReminder.contactId}`, {
          name: selectedReminder.contactName,
          number: selectedReminder.contactNumber,
          extraInfo: currentInfo,
        });
      }

      // 3. Send message if text is present
      if (messageText.trim()) {
        await api.post(`/messages/${ticket.id}`, {
          body: messageText,
        });
      }

      toast.success("Lembrete concluído e mensagem enviada!");
      setSendModalOpen(false);
      history.push(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
    setSending(false);
  };  const [expandedReminders, setExpandedReminders] = useState(false);

  const getLocalDateStr = (d = new Date()) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayStr = getLocalDateStr();

  // Todos os lembretes não concluídos ordenados por urgência (mais antigos/atrasados primeiro)
  const pendingReminders = todayReminders.filter((r) => r.status !== "completed");
  const overdueReminders = pendingReminders.filter((r) => r.date < todayStr);
  const todayOnlyReminders = pendingReminders.filter((r) => r.date === todayStr);
  const futureReminders = pendingReminders.filter((r) => r.date > todayStr);

  const getReminderStatusInfo = (dateStr) => {
    if (!dateStr) return { label: "📅 Agendado", color: "#64748b", bg: "#f1f5f9", formattedDate: "" };
    const parts = dateStr.split("-");
    const formattedDate = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr;

    if (dateStr < todayStr) {
      // Calcula dias de atraso
      const remD = new Date(parts[0], Number(parts[1]) - 1, parts[2]);
      const nowD = new Date();
      const todayD = new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate());
      const diffDays = Math.max(1, Math.round((todayD - remD) / (1000 * 60 * 60 * 24)));
      return {
        label: diffDays === 1 ? "🔴 Atrasado (ontem)" : `🔴 Atrasado (${diffDays}d atrás)`,
        color: "#dc2626",
        bg: "#fef2f2",
        borderColor: "#f87171",
        formattedDate
      };
    } else if (dateStr === todayStr) {
      return {
        label: "🟡 Hoje",
        color: "#d97706",
        bg: "#fffbeb",
        borderColor: "#fbbf24",
        formattedDate
      };
    } else {
      // Futuro
      const remD = new Date(parts[0], Number(parts[1]) - 1, parts[2]);
      const nowD = new Date();
      const todayD = new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate());
      const diffDays = Math.max(1, Math.round((remD - todayD) / (1000 * 60 * 60 * 24)));
      const futureLabel = diffDays === 1 ? "🔵 Amanhã" : `🔵 Em ${diffDays} dias`;
      return {
        label: futureLabel,
        color: "#0284c7",
        bg: "#f0f9ff",
        borderColor: "#60a5fa",
        formattedDate
      };
    }
  };

  const displayedReminders = expandedReminders ? pendingReminders : pendingReminders.slice(0, 5);

  return (
    <div>
      <Container maxWidth="lg" className={classes.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div>
            <Typography component="h2" variant="h6" style={{ fontWeight: "bold", color: "#0f172a" }}>
              📊 Dashboard de Performance & Atendimentos
            </Typography>
            <Typography variant="caption" style={{ color: "#64748b" }}>
              Exibindo dados de: <strong>{activePeriodLabel}</strong> ({activeStartDate} até {activeEndDate})
            </Typography>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {/* SELETOR DE CONEXÃO */}
            <TicketsWhatsappSelect
              selectedWhatsappId={selectedWhatsappId}
              onChange={setSelectedWhatsappId}
            />

            {/* SELETOR DE PERÍODO */}
            <div style={{ minWidth: 160, marginTop: -4 }}>
              <FormControl fullWidth margin="dense">
                <Select
                  displayEmpty
                  variant="outlined"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  <MenuItem dense value="thisMonth">
                    📅 Este mês
                  </MenuItem>
                  <MenuItem dense value="last7">
                    ⚡ Últimos 7 dias
                  </MenuItem>
                  <MenuItem dense value="last15">
                    ⚡ Últimos 15 dias
                  </MenuItem>
                  <MenuItem dense value="last30">
                    ⚡ Últimos 30 dias
                  </MenuItem>
                  <MenuItem dense value="lastMonth">
                    🗓️ Mês anterior
                  </MenuItem>
                  <MenuItem dense value="custom">
                    🎯 Personalizado
                  </MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* SELETORES DE DATA PERSONALIZADA (APENAS QUANDO "custom" FOR ESCOLHIDO) */}
            {selectedPeriod === "custom" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: -4 }}>
                <TextField
                  label="De"
                  type="date"
                  variant="outlined"
                  size="small"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  style={{ width: 140 }}
                />
                <TextField
                  label="Até"
                  type="date"
                  variant="outlined"
                  size="small"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  style={{ width: 140 }}
                />
              </div>
            )}
          </div>
        </div>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.messages.inAttendance.title")}
              </Typography>
              <Grid item>
                <Typography component="h1" variant="h4">
                  {GetTickets("open", "true", "false")}
                </Typography>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.messages.waiting.title")}
              </Typography>
              <Grid item>
                <Typography component="h1" variant="h4">
                  {GetTickets("pending", "true", "false")}
                </Typography>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.messages.closed.title")}
              </Typography>
              <Grid item>
                <Typography component="h1" variant="h4">
                  {GetTickets("closed", "true", "false")}
                </Typography>
              </Grid>
            </Paper>
          </Grid>

          {/* WIDGET: DIMENSIONAMENTO DE CONVERSAS POR CONEXÃO (ATIVAS, FIXADAS, IA, HUMANO) */}
          <Grid item xs={12}>
            <ConnectionsTicketsDashboard />
          </Grid>

          {/* WIDGET: FUNIL DE ATENDIMENTO & CONVERSÃO EM AGENDAMENTOS COM FILTRO DE PERÍODO E CONEXÃO */}
          <Grid item xs={12}>
            <FunnelConversionDashboard
              whatsappId={selectedWhatsappId}
              startDate={activeStartDate}
              endDate={activeEndDate}
            />
          </Grid>

          {/* WIDGET: SATISFAÇÃO DO CLIENTE & NPS (POR CONEXÃO E POR ATENDENTE) */}
          <Grid item xs={12}>
            <NpsQuickOverviewDashboard
              whatsappId={selectedWhatsappId}
              startDate={activeStartDate}
              endDate={activeEndDate}
            />
          </Grid>

          {/* WIDGET: LEMBRETES AGENDADOS (HOJE, ATRASADOS, PRÓXIMOS 3 DIAS) */}
          <Grid item xs={12}>
            <Paper className={classes.remindersPaper} variant="outlined">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Typography component="h3" variant="h6" style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 8 }}>
                  <AlarmIcon color="primary" /> ⏰ Lembretes Agendados
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={fetchReminders}
                  disabled={loadingReminders}
                  startIcon={loadingReminders ? <CircularProgress size={16} /> : null}
                >
                  {loadingReminders ? "Carregando..." : "🔄 Atualizar Lista"}
                </Button>
              </div>

              {/* CONTADORES: HOJE, ATRASADOS, FUTUROS */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                <Paper
                  variant="outlined"
                  style={{
                    flex: 1,
                    minWidth: 140,
                    padding: "10px 14px",
                    backgroundColor: "#fffbeb",
                    borderColor: "#fef3c7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <Typography variant="caption" style={{ color: "#b45309", fontWeight: "bold", display: "block" }}>
                      🟡 Hoje
                    </Typography>
                    <Typography variant="h5" style={{ color: "#92400e", fontWeight: "bold" }}>
                      {loadingReminders ? "..." : todayOnlyReminders.length}
                    </Typography>
                  </div>
                  <Chip label="Hoje" size="small" style={{ backgroundColor: "#fef3c7", color: "#b45309", fontWeight: "bold" }} />
                </Paper>

                <Paper
                  variant="outlined"
                  style={{
                    flex: 1,
                    minWidth: 140,
                    padding: "10px 14px",
                    backgroundColor: "#fef2f2",
                    borderColor: "#fee2e2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <Typography variant="caption" style={{ color: "#b91c1c", fontWeight: "bold", display: "block" }}>
                      🔴 Atrasados
                    </Typography>
                    <Typography variant="h5" style={{ color: "#991b1b", fontWeight: "bold" }}>
                      {loadingReminders ? "..." : overdueReminders.length}
                    </Typography>
                  </div>
                  <Chip label="Atrasados" size="small" style={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: "bold" }} />
                </Paper>

                <Paper
                  variant="outlined"
                  style={{
                    flex: 1,
                    minWidth: 140,
                    padding: "10px 14px",
                    backgroundColor: "#f0f9ff",
                    borderColor: "#e0f2fe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <Typography variant="caption" style={{ color: "#0369a1", fontWeight: "bold", display: "block" }}>
                      🔵 Futuros
                    </Typography>
                    <Typography variant="h5" style={{ color: "#075985", fontWeight: "bold" }}>
                      {loadingReminders ? "..." : futureReminders.length}
                    </Typography>
                  </div>
                  <Chip label="Futuros" size="small" style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: "bold" }} />
                </Paper>
              </div>

              {/* LISTAGEM DE LEMBRETES OU INDICADOR DE CARREGAMENTO */}
              {loadingReminders ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 0", gap: 12 }}>
                  <CircularProgress size={36} color="primary" />
                  <Typography variant="body2" style={{ color: "#64748b", fontWeight: 600 }}>
                    Carregando lembretes agendados em tempo real...
                  </Typography>
                </div>
              ) : pendingReminders.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {displayedReminders.map((rem) => {
                    const statusInfo = getReminderStatusInfo(rem.date);

                    return (
                      <Paper
                        key={rem.id}
                        variant="outlined"
                        style={{
                          padding: 12,
                          backgroundColor: statusInfo.bg,
                          borderLeft: `5px solid ${statusInfo.color}`,
                          borderColor: statusInfo.borderColor,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 8,
                          borderRadius: 8,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 260 }}>
                          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                            <Chip
                              label={statusInfo.label}
                              size="small"
                              style={{
                                backgroundColor: statusInfo.color,
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "0.72rem",
                              }}
                            />
                            <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#0f172a" }}>
                              ⏰ {rem.typeName || "Lembrete"} — 👤 {rem.contactName} ({rem.contactNumber})
                            </Typography>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                            <Typography variant="caption" style={{ color: "#475569", fontWeight: "bold" }}>
                              📅 Data Prevista: {statusInfo.formattedDate}
                            </Typography>
                          </div>
                          {rem.comment && (
                            <Typography variant="body2" style={{ fontSize: "0.85rem", color: "#334155", marginTop: 4, fontStyle: "italic" }}>
                              📝 "{rem.comment}"
                            </Typography>
                          )}
                        </div>

                        <div>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            style={{
                              textTransform: "none",
                              backgroundColor: "#16a34a",
                              color: "#fff",
                              fontWeight: "bold",
                              borderRadius: 6,
                              boxShadow: "0 2px 4px rgba(22, 163, 74, 0.2)",
                            }}
                            onClick={() => handleOpenSendModal(rem)}
                          >
                            💬 Chamar no WhatsApp
                          </Button>
                        </div>
                      </Paper>
                    );
                  })}

                  {/* BOTÃO EXPANDIR / RECOLHER (SE HOUVER MAIS DE 5) */}
                  {pendingReminders.length > 5 && (
                    <div style={{ textAlign: "center", marginTop: 8 }}>
                      <Button
                        variant={expandedReminders ? "contained" : "outlined"}
                        color="primary"
                        size="small"
                        style={{ textTransform: "none", fontWeight: "bold", borderRadius: 8, padding: "6px 16px" }}
                        onClick={() => setExpandedReminders(!expandedReminders)}
                      >
                        {expandedReminders
                          ? "🔼 Mostrar apenas os 5 primeiros"
                          : `🔽 Ver todos os lembretes pendentes (${pendingReminders.length})`}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <Typography variant="body2" style={{ color: "#64748b", padding: "16px 0", textAlign: "center", fontWeight: 500 }}>
                  ✨ Nenhum lembrete pendente no momento. Todos os atendimentos agendados estão em dia!
                </Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper className={classes.fixedHeightPaper}>
              <Chart
                whatsappId={selectedWhatsappId}
                startDate={activeStartDate}
                endDate={activeEndDate}
              />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper className={classes.monthlyChartPaper}>
              <MonthlyChart
                whatsappId={selectedWhatsappId}
                startDate={activeStartDate}
                endDate={activeEndDate}
                periodLabel={activePeriodLabel}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* MODAL DE CONFIRMAÇÃO E ENVIO DE LEMBRETE */}
      <Dialog open={sendModalOpen} onClose={() => setSendModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: "bold" }}>
          💬 Enviar Lembrete para {selectedReminder?.contactName}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ marginBottom: 8, color: "#64748b" }}>
            A mensagem padrão abaixo foi gerada automaticamente. Você pode personalizá-la antes de enviar:
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendModalOpen(false)} disabled={sending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSendReminderMessage}
            color="primary"
            variant="contained"
            disabled={sending}
            style={{ backgroundColor: "#25D366", color: "#fff", fontWeight: "bold" }}
          >
            {sending ? "Enviando..." : "🚀 Enviar no WhatsApp & Abrir Chat"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Dashboard;